
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import { supabaseService } from '../../utils/supabase';

interface EducationItem {
  id: string;
  school: string;
  degree: string;
  major: string;
  description: string;
  gpa?: string;
  image?: string;
}

export default function Education() {
  const [educationData, setEducationData] = useState<EducationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEducationData();
  }, []);

  const loadEducationData = async () => {
    try {
      const data = await supabaseService.getAllData();
      if (data.education && data.education.length > 0) {
        setEducationData(data.education);
      }
    } catch (error) {
      console.error('加载教育数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-slate-200">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Education</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Prepare yourself
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && (!educationData || educationData.length === 0) && (
            <div className="text-center py-12">
              <i className="ri-graduation-cap-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-medium text-gray-600 mb-2">暂无教育经历</h3>
              <p className="text-gray-500">请通过管理后台添加教育经历</p>
            </div>
          )}

          {/* Education Timeline */}
          {!loading && educationData && educationData.length > 0 && (
            <div className="space-y-8">
              {educationData.map((edu) => (
              <Card key={edu.id} className="p-6 hover:shadow-lg transition-shadow" glass>
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Education Info */}
                  <div className="flex-1">
                    <div className="flex items-start space-x-4 mb-4">
                      {/* Timeline dot */}
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                        <i className="ri-graduation-cap-line text-white text-lg"></i>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{edu.school}</h3>
                            <p className="text-blue-600 font-medium">{edu.major} - {edu.degree}</p>
                          </div>
                          <div className="text-right mt-2 md:mt-0">
                            {edu.gpa && <p className="text-sm text-gray-5">GPA: {edu.gpa}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-16">
                      <p className="text-gray-700 leading-relaxed">{edu.description}</p>
                    </div>
                  </div>
                  
                  {/* Education Image */}
                  {edu.image && (
                    <div className="flex justify-center lg:w-28 lg:justify-end flex-shrink-0">
                      <img 
                        src={edu.image} 
                        alt={edu.school}
                        className="h-24 w-24 object-contain rounded-lg border bg-white p-2 shadow-sm"
                      />
                    </div>
                  )}
                </div>
              </Card>
            ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
